import {
  initProStudioUrqlGql,
  SolutionEnvironmentsEnum,
} from '@pypestream/api-services/urql';
import { throwErrorWithId } from '@pypestream/utils';
import { capitalize } from 'lodash';

import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import { sendUserMessage } from '../app.xstate-utils';
import {
  updateSurveyBotStreams,
  UpdateSurveyBotStreamsParams,
} from './helpers/solutions';
import { checkForErrors } from '../../utils/check-for-errors';

export const publishBotVersion = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<void> => {
  if (event.type !== 'publishBot') {
    return;
  }

  const {
    botName: ctxBotName,
    customerId,
    project,
    solutions: bots,
    userInfo,
    draftVersionSolutionData,
    solutionVersions,
    selectedBot,
  } = ctx;

  const projectId = project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  if (
    !userInfo?.id ||
    !userInfo?.email ||
    !userInfo?.firstName ||
    !userInfo?.lastName
  ) {
    throwErrorWithId('User info is required');
    return;
  }

  if (!ctxBotName) {
    throwErrorWithId('Bot Name is required');
    return;
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  const uniqueCustomerId = project?.projectProStudioSetting?.customerId;

  if (!uniqueCustomerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  const uniqueSolutionId =
    selectedBot?.uniqueId || solutionVersions?.[0]?.solutionId;

  if (!uniqueSolutionId) {
    throwErrorWithId('Solution unique ID is required');
    return;
  }

  const {
    version,
    republish,
    env: environment,
    botType,
    bots: eventBots,
  } = event.payload;

  if (!version) {
    throwErrorWithId('Version is required');
    return;
  }

  const botId = `${customerId}.${ctxBotName}`;

  const urqlGql = initProStudioUrqlGql({
    userId: userInfo.id,
    userEmail: userInfo.email,
    userFirstName: userInfo.firstName,
    userLastName: userInfo.lastName,
  });

  if (botType === 'survey') {
    await updateSurveyBotStreams({
      bots,
      eventBots,
      environment,
      uniqueCustomerId,
      projectId,
      botId,
      userInfo: userInfo as UpdateSurveyBotStreamsParams['userInfo'],
    });
  }

  const botSolutionVersionId = `${botId}.${version}`;

  const getRepublishVersionId = () => {
    const matchingVersion = solutionVersions.find(
      (v) => v?.botSolutionVersionId === botSolutionVersionId
    );
    return matchingVersion?.id;
  };

  const solutionVersionId = republish
    ? getRepublishVersionId()
    : draftVersionSolutionData?.id;

  const basePayload = {
    doVersionBump: !republish,
    environment: capitalize(environment) as SolutionEnvironmentsEnum,
  };

  let publishSolutionVersionResponse;

  if (!solutionVersionId) {
    publishSolutionVersionResponse =
      await urqlGql.createAndPublishSolutionVersion({
        ...basePayload,
        solutionId: uniqueSolutionId,
        botSolutionVersionId,
      });
  } else {
    publishSolutionVersionResponse = await urqlGql.publishSolutionVersion({
      ...basePayload,
      solutionVersionId,
    });
  }

  try {
    checkForErrors(publishSolutionVersionResponse);
  } catch (error) {
    // Show toast message for error without breaking xstate success flow
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Error publishing solution version';

    sendUserMessage({
      type: 'error',
      text: `Error publishing solution version: ${errorMessage}`,
      autoClose: 5000,
    });
  }

  // Always run callback to close modal and refetch data, even if there were errors
  event.payload.cb();
};
